import { LocalStorageKeys } from '@constants/enums';
import { twoFactorCookieDuration } from '@constants/twoFactor';
import authSvc from '@efex-services/auth/authSvc';
import { SetUpTwoFactorStep2Response } from '@efex-services/auth/types';
import { client } from '@efex-services/config';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIntercom } from 'react-use-intercom';
import { useApiCall } from 'use-api-call';

interface ConfirmCodeProps {
  show90DaysCheckbox?: boolean;
  isValidationStep?: boolean;
  goBack?: () => void;
  isValidated?: () => void;
  nextStep?: (values: { refresh: string; token: string }) => void;
}

const ConfirmCode = ({
  show90DaysCheckbox,
  isValidationStep = false,
  goBack,
  isValidated,
  nextStep,
}: ConfirmCodeProps) => {
  const { t } = useTranslation();
  const { showMessages } = useIntercom();
  const [authCode, setAuthCode] = useState('');
  const [disableRequestCode, setDisableRequestCode] = useState(false);

  const {
    data,
    invoke: verifyTwoFactorCode,
    loading,
    error,
  } = useApiCall(() =>
    authSvc.verifyTwoFactorCode({
      otp: authCode,
      days: disableRequestCode ? twoFactorCookieDuration : undefined,
    })
  );

  const {
    data: confirmOTPData,
    invoke: confirmTwoFactorQrCode,
    loading: confirmLoading,
    error: confirmError,
  }: {
    data: SetUpTwoFactorStep2Response;
    invoke: () => void;
    loading: boolean;
    error: Error | undefined;
  } = useApiCall(() => authSvc.confirmTwoFactorQrCode(authCode));

  const allowOnlyNumber = (value: string) => value.replace(/\D/g, '');

  useEffect(() => {
    if (data?.refresh && data?.token) {
      try {
        nextStep?.({ refresh: data.refresh, token: data.token });
        localStorage.setItem(LocalStorageKeys.TOKEN, data.token || '');
        localStorage.setItem(LocalStorageKeys.REFRESH, data.refresh || '');
        client.defaults.headers.common = {
          Authorization: `Bearer ${data.token}`,
        };
      } catch (err) {
        console.error(err);
      }
    }
    if (confirmOTPData?.refresh && confirmOTPData?.token) {
      isValidated?.();
      localStorage.setItem(LocalStorageKeys.TOKEN, confirmOTPData.token || '');
      localStorage.setItem(
        LocalStorageKeys.REFRESH,
        confirmOTPData.refresh || ''
      );
      client.defaults.headers.common = {
        Authorization: `Bearer ${confirmOTPData.token}`,
      };
    }
  }, [data, confirmOTPData, nextStep, isValidated]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="body2">{t('tfConfirmationCode')}</Typography>
        <TextField
          type="text"
          label={t('tfAuthCodeInput')}
          variant="outlined"
          className="w-full !my-3"
          value={authCode}
          error={!!error || !!confirmError}
          helperText={
            error || confirmError ? t('tfConfirmationCodeError') : undefined
          }
          onChange={(e) =>
            setAuthCode(allowOnlyNumber(e.target.value).slice(0, 6))
          }
        />
        {show90DaysCheckbox ? (
          <FormControlLabel
            control={
              <Checkbox
                checked={disableRequestCode}
                onChange={(_, value) => setDisableRequestCode(value)}
              />
            }
            label={t('tfDisable90Days', { days: twoFactorCookieDuration })}
          />
        ) : null}
        {isValidationStep ? (
          // eslint-disable-next-line jsx-a11y/anchor-is-valid
          <a onClick={showMessages} href="#" className="block pt-2">
            <Typography variant="subtitle2">
              {t('tfConfirmationCodeRecovery')}
            </Typography>
          </a>
        ) : null}

        <div
          className={clsx(
            'flex gap-3 mt-2',
            goBack ? 'justify-around' : 'justify-end'
          )}
        >
          {goBack ? (
            <Button
              sx={{ borderColor: '#00143A', color: '#00143A' }}
              variant="outlined"
              onClick={goBack}
            >
              {t('back')}
            </Button>
          ) : null}
          <Button
            sx={{ backgroundColor: '#00143A' }}
            variant="contained"
            onClick={
              isValidationStep ? verifyTwoFactorCode : confirmTwoFactorQrCode
            }
            startIcon={
              loading || confirmLoading ? (
                <RefreshIcon className="animate-spin" />
              ) : null
            }
            disabled={
              !authCode || authCode.length < 6 || loading || confirmLoading
            }
          >
            {t('verify')}
          </Button>
        </div>
      </Grid>
    </Grid>
  );
};

export default ConfirmCode;
